import React, { useEffect, useMemo, useRef } from "react";
import { Link } from "react-router-dom";
import { useSeo } from "@/hooks/useSeo";
import { SEO } from "@/data/seo";
import { SITE, CATEGORIES, PRINCIPLES } from "@/data/config";
import { SERVICES } from "@/data/services";
import { INDUSTRIES } from "@/data/industries";
import { NEEDS } from "@/data/needs";
import { PORTFOLIO } from "@/data/portfolio";
import { Overline, PrimaryButton, SecondaryButton } from "@/components/Primitives";
import ServiceCard from "@/components/ServiceCard";
import PortfolioCover from "@/components/PortfolioCover";
import Marquee from "@/components/Marquee";
import CTABlock from "@/components/CTABlock";
import { FAQS } from "@/data/faqs";
import { HOME_HELP_FIX, HOME_HOW_TO_START, LEARN_MORE } from "@/data/educational";
import LearnMoreCard from "@/components/LearnMoreCard";
import GrowthLinks from "@/components/GrowthLinks";
import SideRobot from "@/components/SideRobot";
import Reveal from "@/components/Reveal";
import { organizationSchema, websiteSchema, webPageSchema, faqPageSchema } from "@/lib/schemas";
import { categoryHref } from "@/lib/categoryRoute";

const featuredCategories = [
  "strategy-and-audits",
  "brand-systems",
  "website-systems",
  "brand-and-website-packages",
  "local-visibility",
  "marketing-support",
];

const Home = () => {
  useSeo({
    ...SEO.home,
    jsonLd: [
      organizationSchema(),
      websiteSchema(),
      webPageSchema({ path: "/", name: SEO.home.title, description: SEO.home.description }),
      faqPageSchema(FAQS.map((f) => ({ q: f.q, a: f.a }))),
    ],
  });
  const featuredServices = useMemo(
    () =>
      [
        "strategy-call",
        "brand-foundation-core",
        "website-growth-build",
        "brand-and-website-growth-system",
        "google-business-profile-optimization",
        "complete-business-presence-system",
      ]
        .map((slug) => SERVICES.find((s) => s.slug === slug))
        .filter(Boolean),
    []
  );
  const otherIndustries = useMemo(
    () => INDUSTRIES.filter((i) => i.slug !== "real-estate"),
    []
  );
  const realEstate = useMemo(
    () => INDUSTRIES.find((i) => i.slug === "real-estate"),
    []
  );

  // Ambient parallax on hero + brandscape images. Ref-based transform so we
  // never trigger a React re-render on scroll. Respects prefers-reduced-motion.
  const heroImgRef = useRef(null);
  const heroSectionRef = useRef(null);
  const brandscapeImgRef = useRef(null);
  const brandscapeSectionRef = useRef(null);

  useEffect(() => {
    if (typeof window === "undefined") return;
    const reduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (reduced) return;

    let ticking = false;
    const apply = () => {
      ticking = false;
      const sy = window.scrollY || window.pageYOffset;

      // Hero: as user scrolls DOWN the page, image drifts UP slowly.
      if (heroImgRef.current && heroSectionRef.current) {
        const rect = heroSectionRef.current.getBoundingClientRect();
        // Only translate while any part of hero is on/near screen.
        if (rect.bottom > -200 && rect.top < window.innerHeight + 200) {
          const y = sy * 0.18; // slow drift
          heroImgRef.current.style.transform = `translate3d(0, ${y}px, 0) scale(1.12)`;
        }
      }

      // Brandscape: same idea, gentler. Image parallaxes relative to the
      // section's own offset so it only moves while visible.
      if (brandscapeImgRef.current && brandscapeSectionRef.current) {
        const rect = brandscapeSectionRef.current.getBoundingClientRect();
        if (rect.bottom > -200 && rect.top < window.innerHeight + 200) {
          const progress = (window.innerHeight - rect.top) * 0.14;
          brandscapeImgRef.current.style.transform = `translate3d(0, ${progress * -0.4}px, 0) scale(1.12)`;
        }
      }
    };

    const onScroll = () => {
      if (!ticking) {
        window.requestAnimationFrame(apply);
        ticking = true;
      }
    };
    apply();
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", apply);
    return () => {
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", apply);
    };
  }, []);

  return (
    <main data-testid="home-page">
      {/* Global side-mounted feature mascot. Fixed left edge, halfway vertical,
          rotated 90° right so the top of the robot faces into the page. */}
      <SideRobot />

      {/* HERO — Xactly-caliber full-bleed cinematic. Copy overlays the image with a strong left-side gradient. */}
      <section
        ref={heroSectionRef}
        className="relative bg-black overflow-hidden border-b border-white/10"
        data-testid="hero"
      >
        {/* Full-bleed image with ambient parallax */}
        <div className="absolute inset-0 overflow-hidden">
          <img
            ref={heroImgRef}
            src="/hero/kostaja-signage.png"
            alt="Cinematic black and yellow Kostaja brand entrance with illuminated logo signage."
            className="absolute inset-0 w-full h-full object-cover will-change-transform"
            style={{ transform: "translate3d(0, 0, 0) scale(1.12)" }}
            fetchpriority="high"
          />
          {/* Left-heavy dark gradient for text readability */}
          <div
            className="absolute inset-0 pointer-events-none"
            aria-hidden
            style={{
              background:
                "linear-gradient(90deg, rgba(0,0,0,0.92) 0%, rgba(0,0,0,0.82) 35%, rgba(0,0,0,0.45) 60%, rgba(0,0,0,0.20) 100%)",
            }}
          />
          {/* Vertical top+bottom fade for editorial framing */}
          <div
            className="absolute inset-0 pointer-events-none"
            aria-hidden
            style={{
              background:
                "linear-gradient(180deg, rgba(0,0,0,0.35) 0%, rgba(0,0,0,0) 20%, rgba(0,0,0,0) 70%, rgba(0,0,0,0.45) 100%)",
            }}
          />
        </div>

        {/* Content */}
        <div className="relative min-h-[86vh] lg:min-h-[92vh] flex flex-col">
          {/* Copy block */}
          <div className="flex-1 flex items-center">
            <div className="max-w-[1440px] mx-auto w-full px-5 lg:px-10 py-16 lg:py-24 pt-24 lg:pt-32">
              <div className="max-w-3xl">
                <div
                  data-testid="hero-eyebrow"
                  className="inline-flex items-center gap-3 text-[11px] uppercase tracking-[0.32em] font-black text-[#FFC600]"
                >
                  <span className="block w-8 h-px bg-[#FFC600]" aria-hidden />
                  <span>Brand, Web &amp; Marketing for Service Businesses</span>
                </div>

                <h1
                  data-testid="hero-headline"
                  className="mt-6 lg:mt-8 font-black tracking-[-0.035em] leading-[0.88] text-white"
                  style={{ fontSize: "clamp(52px, 8vw, 128px)" }}
                >
                  Make your business easier to <span className="k-yellow">find, trust, and choose.</span>
                </h1>

                <p
                  data-testid="hero-supporting"
                  className="mt-8 lg:mt-10 text-lg lg:text-2xl leading-[1.35] font-medium text-white/90 max-w-2xl"
                >
                  Kostaja builds the website, brand, local search presence, content, and sales tools behind stronger service businesses across Ottawa, the Ottawa Valley, and Ontario.
                </p>

                {/* Availability layer — restrained, sits above the CTAs */}
                <div
                  data-testid="hero-availability"
                  className="mt-8 lg:mt-10 pl-4 border-l-2 border-[#FFC600] max-w-xl"
                >
                  <p className="text-[11px] uppercase tracking-[0.22em] font-black text-[#FFC600]">
                    2026 project capacity is allocated
                  </p>
                  <p className="mt-2 text-sm lg:text-base text-white/85 leading-relaxed">
                    Now reviewing select projects for 2027.
                  </p>
                </div>

                <div className="mt-8 lg:mt-10 flex flex-wrap items-center gap-4 lg:gap-5">
                  <PrimaryButton to="/waitlist" testId="hero-primary-cta">
                    Apply to Work With Kostaja
                  </PrimaryButton>
                  <SecondaryButton to="/portfolio" testId="hero-secondary-cta">
                    View Selected Work
                  </SecondaryButton>
                </div>

                <p className="mt-5 text-xs text-white/60 max-w-xl leading-relaxed">
                  Waitlisted projects receive priority consideration if an earlier opening becomes available.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Marquee
        items={[
          "Outcomes before methods",
          "Plain English",
          "One accountable partner",
          "Guided from first call to launch",
        ]}
      />

      {/* SECTION · CAPACITY — 2026 allocated / 2027 applications open (editorial moment) */}
      <section
        data-testid="home-capacity"
        className="relative bg-black border-b border-white/10 overflow-hidden"
      >
        <div className="max-w-[1440px] mx-auto px-5 lg:px-10 py-20 lg:py-32 grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-14 items-center">
          <div className="lg:col-span-7">
            <div className="inline-flex items-center gap-3 text-[11px] uppercase tracking-[0.32em] font-black text-[#FFC600]">
              <span className="block w-8 h-px bg-[#FFC600]" aria-hidden />
              <span>2026 capacity</span>
            </div>
            <h2
              className="mt-6 font-black tracking-[-0.04em] leading-[0.82] text-white"
              style={{ fontSize: "clamp(64px, 9.5vw, 148px)" }}
            >
              <span className="block">2026</span>
              <span className="block k-yellow">Allocated.</span>
            </h2>
          </div>
          <div className="lg:col-span-5">
            <p className="text-lg lg:text-xl text-white/85 leading-[1.5] max-w-md">
              We deliberately limit the number of client relationships Kostaja takes on each year. It keeps us involved in the work, accountable to the outcome, and able to give serious projects the attention they deserve.
            </p>
            <p className="mt-6 text-base text-white/70 max-w-md leading-relaxed">
              Applications for 2027 are now open. Waitlisted projects receive priority consideration if an earlier opening becomes available.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <PrimaryButton to="/waitlist" testId="capacity-apply-cta">
                Apply for 2027 →
              </PrimaryButton>
              <Link
                to="/waitlist#how"
                data-testid="capacity-learn-cta"
                className="text-sm font-semibold text-white/85 hover:text-[#FFC600] underline underline-offset-[6px] decoration-white/25 hover:decoration-[#FFC600] transition-colors self-center"
              >
                How the waitlist works →
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* CLIENT LOGO BAND — real trust proof, no fake claims */}
      <section
        data-testid="home-client-proof"
        className="relative bg-black border-b border-white/10 overflow-hidden"
      >
        <div className="max-w-[1440px] mx-auto px-5 lg:px-10 py-14 lg:py-20">
          <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-4 mb-10">
            <div>
              <Overline className="mb-3">Named clients</Overline>
              <p className="text-2xl lg:text-4xl font-black tracking-[-0.02em] leading-[1.05] max-w-3xl">
                Real work with real businesses across auto, real estate, food, professional services, and owned Kostaja brands.
              </p>
            </div>
            <SecondaryButton to="/portfolio" testId="home-client-proof-cta">Open the portfolio</SecondaryButton>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-2 lg:gap-3">
            {["multi-service-auto-shop","real-estate-marketing-subbrand","independent-mortgage-broker","seasonal-food-stand-brand","ai-receptionist-brand","home-services-booking-brand","dealership-support-brand","capital-access-brand","operator-media-brand","local-detailing-brand"]
              .map((slug) => PORTFOLIO.find((p) => p.slug === slug))
              .filter(Boolean)
              .map((p) => (
                <Link
                  key={p.slug}
                  to={`/portfolio/${p.slug}`}
                  data-testid={`home-logo-${p.slug}`}
                  className="group block aspect-[4/3] border border-white/10 overflow-hidden hover:border-[#FFC600] transition-colors"
                  aria-label={`Open ${p.client || p.title} case study`}
                >
                  <PortfolioCover item={p} accent="yellow" />
                </Link>
              ))}
          </div>
        </div>
      </section>

      {/* THE GAP · diagnostic section (Part 6) */}
      <section
        data-testid="home-the-gap"
        className="bg-black border-b border-white/10"
      >
        <div className="max-w-[1440px] mx-auto px-5 lg:px-10 py-24 lg:py-36">
          <Overline color="yellow" className="mb-8">The problem</Overline>
          <h2 className="text-4xl sm:text-6xl lg:text-[80px] font-black tracking-[-0.025em] leading-[0.95] max-w-5xl">
            The business is good. The first impression should prove it.
          </h2>
          <p className="mt-10 max-w-2xl text-lg text-white/70 leading-relaxed">
            The work may be strong. The operation may be real. If the website, brand, content, Google presence, sales materials, and follow-up do not support that standard, buyers feel the gap before they understand the business.
          </p>
          <div className="mt-16 grid grid-cols-1 lg:grid-cols-2 gap-px bg-white/10 border border-white/15">
            <div className="bg-black p-10 lg:p-14">
              <div className="text-[11px] uppercase tracking-[0.22em] font-bold text-white/50 mb-8">Inside the business</div>
              <ul className="space-y-4">
                {["Real capability", "Good work", "Repeat customers", "Hard-earned trust", "Strong service", "Operational knowledge"].map((line) => (
                  <li key={line} className="text-2xl lg:text-3xl font-black tracking-[-0.02em] text-white leading-tight">{line}</li>
                ))}
              </ul>
            </div>
            <div className="bg-black p-10 lg:p-14 relative">
              <div className="text-[11px] uppercase tracking-[0.22em] font-bold text-white/50 mb-8">Outside the business</div>
              <ul className="space-y-4">
                {["Unclear website", "Weak first impression", "Scattered materials", "Thin service pages", "Inconsistent content", "Missed follow-up"].map((line) => (
                  <li key={line} className="text-2xl lg:text-3xl font-black tracking-[-0.02em] text-white/40 leading-tight">{line}</li>
                ))}
              </ul>
            </div>
          </div>
          <p className="mt-16 text-2xl lg:text-4xl font-black tracking-[-0.025em] leading-[1.05] max-w-3xl">
            Kostaja builds the system between the two.
          </p>
        </div>
      </section>

      {/* SECTION 3 · POSITIONING STATEMENT — editorial, no cards */}
      <section
        data-testid="home-positioning"
        className="bg-black border-b border-white/10"
      >
        <div className="max-w-[1440px] mx-auto px-5 lg:px-10 py-24 lg:py-40">
          <div className="max-w-6xl">
            <Overline className="mb-8">Positioning</Overline>
            <p className="text-4xl sm:text-6xl lg:text-[88px] font-black tracking-[-0.03em] leading-[0.94] text-white">
              Your business shouldn&apos;t look smaller than it is.
            </p>
            <p className="mt-10 max-w-2xl text-lg lg:text-xl text-white/70 leading-relaxed">
              Kostaja works with owners whose company has outgrown how it appears in public. The work is good. The website, the brand, the sales materials, the first impression should be too.
            </p>
          </div>
        </div>
      </section>

      {/* SECTION 4 · BRANDSCAPE — cinematic full-bleed bridge dramatizing the slogan */}
      <section
        ref={brandscapeSectionRef}
        data-testid="home-brandscape"
        className="relative bg-black border-b border-white/10 overflow-hidden"
      >
        <div className="relative w-full min-h-[75vh] lg:min-h-[92vh] overflow-hidden">
          <video
            ref={brandscapeImgRef}
            poster="/hero/kostaja-brandscape.png"
            autoPlay
            muted
            loop
            playsInline
            preload="auto"
            disablePictureInPicture
            controlsList="nodownload nofullscreen noremoteplayback"
            aria-label="A day in the visual system: a city block imagined fully in the Kostaja brand."
            className="absolute inset-0 w-full h-full object-cover will-change-transform motion-reduce:hidden"
            style={{ transform: "translate3d(0, 0, 0) scale(1.12)" }}
          >
            <source src="/video/kostaja-brandscape.webm" type="video/webm" />
            <source src="/video/kostaja-brandscape.mp4" type="video/mp4" />
          </video>
          {/* Reduced-motion fallback: static poster only */}
          <img
            src="/hero/kostaja-brandscape.png"
            alt="A city block imagined fully in the Kostaja brand: signage, banners, storefront, transit, and street team all coherent under one visual system."
            className="absolute inset-0 w-full h-full object-cover hidden motion-reduce:block"
            loading="lazy"
          />
          {/* Bottom-heavy dark gradient so caption reads clean */}
          <div
            aria-hidden
            className="absolute inset-0 pointer-events-none"
            style={{
              background:
                "linear-gradient(180deg, rgba(0,0,0,0.55) 0%, rgba(0,0,0,0.15) 30%, rgba(0,0,0,0.15) 55%, rgba(0,0,0,0.85) 100%)",
            }}
          />
          {/* Overlay copy block — restrained. Image speaks first. */}
          <div className="absolute inset-x-0 bottom-0">
            <div className="max-w-[1440px] mx-auto px-5 lg:px-10 pb-12 lg:pb-20">
              <div className="max-w-4xl">
                <h2
                  className="font-black tracking-[-0.03em] leading-[0.88] text-white"
                  style={{ fontSize: "clamp(44px, 6.5vw, 104px)" }}
                >
                  Shape Your <span className="k-yellow">Brandscape.</span>
                </h2>
                <p className="mt-6 lg:mt-8 max-w-2xl text-base lg:text-xl text-white/85 leading-[1.4] font-medium">
                  Everywhere your business is seen becomes part of the impression. Kostaja treats the whole picture as one canvas.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* SECTION 5 · WHAT WE DO — four macro-capabilities, editorial */}
      <section
        data-testid="home-what-we-do"
        className="bg-black border-b border-white/10"
      >
        <div className="max-w-[1440px] mx-auto px-5 lg:px-10 py-20 lg:py-28">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 mb-14">
            <Reveal className="lg:col-span-6" y={20}>
              <Overline color="yellow" className="mb-4">Core services</Overline>
              <h2 className="text-4xl lg:text-6xl font-black tracking-[-0.02em] leading-[0.95]">
                What needs to work before the customer says yes.
              </h2>
            </Reveal>
            <Reveal className="lg:col-span-6 lg:pt-8" y={20} delay={80}>
              <p className="text-white/70 max-w-lg text-base lg:text-lg leading-relaxed">
                You do not need to diagnose your own problem before you get in touch. Tell us the outcome. We name the sequence.
              </p>
            </Reveal>
          </div>
          <ul className="grid grid-cols-1 md:grid-cols-2 gap-px bg-white/10 border border-white/15">
            {[
              {
                n: "01",
                to: "/services/brand",
                title: "Brand",
                desc: "Build a business people recognize, understand, and trust.",
              },
              {
                n: "02",
                to: "/services/websites",
                title: "Websites",
                desc: "A website that looks the part, explains the business, and earns the next step.",
              },
              {
                n: "03",
                to: "/services/marketing",
                title: "Marketing",
                desc: "Put the business in front of the right people and give them a reason to act.",
              },
              {
                n: "04",
                to: "/services/ongoing-partner",
                title: "Ongoing Partner",
                desc: "Senior marketing and creative support without building the whole department in-house.",
              },
            ].map((it, i) => (
              <Reveal as="li" key={it.n} className="bg-black" y={28} delay={120 + i * 110}>
                <Link
                  to={it.to}
                  data-testid={`home-macro-${it.n}`}
                  className="group block p-8 lg:p-12 h-full hover:bg-[#FFC600] hover:text-black transition-colors"
                >
                  <div className="flex items-baseline justify-between gap-4 mb-8">
                    <span className="text-[11px] uppercase tracking-[0.22em] font-bold opacity-70">{it.n}</span>
                    <span className="text-[11px] uppercase tracking-[0.22em] font-bold opacity-70">Start here</span>
                  </div>
                  <h3 className="text-4xl lg:text-6xl font-black tracking-[-0.02em] leading-[0.95]">
                    {it.title}
                  </h3>
                  <p className="mt-6 text-base lg:text-lg leading-relaxed opacity-85 max-w-md">
                    {it.desc}
                  </p>
                  <span className="mt-10 inline-flex items-center gap-2 text-sm font-semibold">
                    Open the path <span aria-hidden>→</span>
                  </span>
                </Link>
              </Reveal>
            ))}
          </ul>
          <div className="mt-10 flex justify-center">
            <p className="text-sm text-white/55 max-w-xl text-center">
              Not sure where to start? <Link to="/contact" data-testid="home-what-we-do-diag" className="text-[#FFC600] font-semibold hover:text-white">Show us what&apos;s not working.</Link>
            </p>
          </div>
        </div>
      </section>

      {/* START WHERE YOU ARE · elegant routing (Part 7) */}
      <section
        data-testid="home-start-where-you-are"
        className="bg-black border-b border-white/10"
      >
        <div className="max-w-[1440px] mx-auto px-5 lg:px-10 py-20 lg:py-28">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 mb-14">
            <div className="lg:col-span-7">
              <Overline color="yellow" className="mb-4">Start where you are</Overline>
              <h2 className="text-4xl lg:text-6xl font-black tracking-[-0.02em] leading-[0.95]">
                Choose the path that matches the moment.
              </h2>
            </div>
          </div>
          <ul className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-px bg-white/10 border border-white/15">
            {[
              { to: "/needs", eyebrow: "By stage", title: "Starting, launching, outgrowing, or rebuilding", cta: "Choose by Stage" },
              { to: "/needs", eyebrow: "By problem", title: "Unclear website, weak trust signals, local visibility gaps", cta: "Choose by Problem" },
              { to: "/industries", eyebrow: "By industry", title: "Real estate, contractors, home services, restaurants, professional services", cta: "Choose by Industry" },
              { to: "/services", eyebrow: "By service", title: "Brand, website, local search, content, sales materials, ongoing support", cta: "Choose by Service" },
            ].map((r) => (
              <li key={r.eyebrow} className="bg-black">
                <Link
                  to={r.to}
                  data-testid={`start-${r.eyebrow.replace(/\s/g, "-").toLowerCase()}`}
                  className="group block p-8 lg:p-10 h-full hover:bg-[#FFC600] hover:text-black transition-colors"
                >
                  <div className="text-[11px] uppercase tracking-[0.22em] font-bold opacity-70 mb-8">{r.eyebrow}</div>
                  <p className="text-lg lg:text-xl font-semibold leading-snug mb-10">{r.title}</p>
                  <span className="inline-flex items-center gap-2 text-sm font-semibold">
                    {r.cta} <span aria-hidden>→</span>
                  </span>
                </Link>
              </li>
            ))}
          </ul>
          <div className="mt-12 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-6 pt-10 border-t border-white/10">
            <div>
              <div className="text-[11px] uppercase tracking-[0.22em] font-bold text-white/50 mb-2">If unsure</div>
              <p className="text-lg lg:text-xl font-semibold max-w-xl">
                Something is not working, but you do not know what to fix first?
              </p>
            </div>
            <PrimaryButton to="/waitlist" testId="start-if-unsure">
              Apply to Work With Kostaja
            </PrimaryButton>
          </div>
        </div>
      </section>

      {/* SERVICES-AT-A-GLANCE STRIP (immediately after hero — 10-second clarity) */}
      <section
        data-testid="home-service-strip"
        className="hidden"
      >
        <div className="max-w-[1440px] mx-auto px-5 lg:px-10 py-10 lg:py-14">
          <div className="flex items-baseline justify-between gap-6 mb-6">
            <Overline color="yellow">What Kostaja does</Overline>
            <Link to="/services" className="text-xs font-semibold text-[#FFC600] underline underline-offset-4 hover:text-white">
              All services →
            </Link>
          </div>
          <ul className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-px bg-white/10 border border-white/15">
            {[
              { label: "Branding", to: "/services/brand", desc: "Look and sound as capable as you are." },
              { label: "Websites", to: "/services/websites", desc: "A site visitors understand and act on." },
              { label: "Local Search", to: "/services/marketing", desc: "Get found by nearby customers at the moment of intent." },
              { label: "Content & Social", to: "/services/marketing", desc: "Show up consistently. Stay top-of-mind." },
              { label: "Sales Materials", to: "/services/print-sales-materials", desc: "Follow-ups that close, not just get sent." },
              { label: "Connected Systems", to: "/services/marketing-systems", desc: "Every lead answered. Every enquiry followed up." },
            ].map((s) => (
              <li key={s.label} className="bg-black">
                <Link
                  to={s.to}
                  data-testid={`svc-strip-${s.label.toLowerCase().replace(/\s|&/g, "-")}`}
                  className="block h-full p-5 lg:p-6 group hover:bg-white/5 transition-colors"
                >
                  <div className="text-base lg:text-lg font-black tracking-tight text-white group-hover:text-[#FFC600]">
                    {s.label}
                  </div>
                  <div className="mt-2 text-xs text-white/65 leading-snug">{s.desc}</div>
                </Link>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* WHY KOSTAJA */}
      <section className="bg-black border-b border-white/10" data-testid="philosophy">
        <div className="max-w-[1440px] mx-auto px-5 lg:px-10 py-20 lg:py-28 grid grid-cols-1 lg:grid-cols-12 gap-10">
          <div className="lg:col-span-5">
            <Overline className="mb-4">Why Kostaja</Overline>
            <h2 className="text-4xl lg:text-6xl font-black tracking-tighter leading-[0.95]">
              Good businesses lose opportunities when the outside does not match the inside.
            </h2>
            <p className="mt-6 text-white/70 max-w-md">
              We close that gap. Brand that earns trust. Websites that explain the offer. Local visibility that helps the right people find you.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <SecondaryButton to="/about" testId="philosophy-about">Read the studio brief</SecondaryButton>
            </div>
          </div>
          <div className="lg:col-span-7">
            <ul className="grid grid-cols-1 sm:grid-cols-2 gap-px bg-white/10 border border-white/15">
              {PRINCIPLES.map((p, idx) => (
                <li
                  key={p}
                  data-testid={`principle-${idx}`}
                  className="bg-black p-6 lg:p-8 flex items-start gap-4"
                >
                  <span className="text-[10px] font-black k-yellow leading-none mt-1">0{idx + 1}</span>
                  <span className="text-base lg:text-lg font-semibold leading-snug">{p}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* WHO KOSTAJA IS BEST FOR + HONEST FIT */}
      <section className="bg-black border-b border-white/10" data-testid="home-fit">
        <div className="max-w-[1440px] mx-auto px-5 lg:px-10 py-20 lg:py-28 grid grid-cols-1 lg:grid-cols-12 gap-10">
          <div className="lg:col-span-5">
            <Overline className="mb-4">Who Kostaja is best for</Overline>
            <h2 className="text-4xl lg:text-6xl font-black tracking-tight leading-[0.95]">
              Established service businesses ready for a stronger public presence.
            </h2>
            <p className="mt-6 text-white/75 max-w-md text-base lg:text-lg leading-relaxed">
              Trades and contractors, MEP businesses, automotive, home services, real estate and mortgage professionals, clinics and medical practices, hospitality, salons, local retail, and independent professional services. Canadian-based, working with clients across Canada and North America.
            </p>
          </div>
          <div className="lg:col-span-7 space-y-6">
            <div className="border border-white/15 p-6 lg:p-7">
              <div className="text-[10px] uppercase tracking-[0.22em] font-bold k-yellow mb-2">Best fit</div>
              <p className="text-white/85 leading-relaxed">
                Owners whose company operates at a higher standard than the current website, brand, or sales materials suggest. Companies rebranding, relaunching, expanding, or working to sharpen their position in a competitive local market.
              </p>
            </div>
            <div data-testid="home-not-a-fit" className="border border-white/15 p-6 lg:p-7 bg-black/40">
              <div className="text-[10px] uppercase tracking-[0.22em] font-bold text-white/60 mb-2">A quick note on fit</div>
              <p className="text-white/80 leading-relaxed">
                Kostaja is built for businesses that care about quality, long-term value, professional presentation, and having the work handled properly by one team. It is less suited to buyers choosing purely on lowest price, or to teams looking for isolated short-term tactics without addressing the brand and business underneath them.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* WHAT WORKING WITH KOSTAJA IS LIKE (premium / white-glove) */}
      <section data-testid="home-experience" className="bg-black border-b border-white/10">
        <div className="max-w-[1440px] mx-auto px-5 lg:px-10 py-20 lg:py-28">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 mb-10">
            <div className="lg:col-span-6">
              <Overline color="yellow" className="mb-4">What working with Kostaja is like</Overline>
              <h2 className="text-4xl lg:text-6xl font-black tracking-tighter leading-[0.95]">
                One team. Guided from first call to launch.
              </h2>
            </div>
            <div className="lg:col-span-6 lg:pt-10">
              <p className="text-white/75 max-w-lg text-base lg:text-lg leading-relaxed">
                Kostaja runs strategy, design, copy, build, coordination, QA, and launch through one accountable process.
              </p>
            </div>
          </div>
          <ul className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-px bg-white/10 border border-white/15">
            {[
              { n: "01", t: "One partner", d: "A single accountable team rather than multiple disconnected vendors to manage." },
              { n: "02", t: "Guided from the start", d: "We help identify what the business actually needs before prescribing deliverables." },
              { n: "03", t: "Managed execution", d: "Strategy, design, copy, build, coordination, QA, launch, and agreed follow-up in one process." },
              { n: "04", t: "Transparent scope", d: "Clients understand what is included, what is optional, what it costs, and what happens next." },
              { n: "05", t: "Built around the business", d: "Recommendations shaped by your operation, market, audience, and commercial goals." },
            ].map((it) => (
              <li key={it.n} data-testid={`exp-${it.n}`} className="bg-black p-6 lg:p-7 flex flex-col gap-3">
                <span className="text-[10px] font-black k-yellow leading-none">{it.n}</span>
                <span className="text-xl lg:text-2xl font-black tracking-tight leading-tight text-white">{it.t}</span>
                <span className="text-sm text-white/75 leading-snug">{it.d}</span>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* WHAT THIS SITE HELPS FIX */}
      <section className="hidden" data-testid="home-help-fix">
        <div className="max-w-[1440px] mx-auto px-5 lg:px-10 py-20 lg:py-28 grid grid-cols-1 lg:grid-cols-12 gap-10">
          <div className="lg:col-span-5">
            <Overline className="mb-4">What this site helps fix</Overline>
            <h2 className="text-4xl lg:text-6xl font-black tracking-tight leading-[0.95]">
              Six common outcomes. One right starting point for you.
            </h2>
            <p className="mt-6 text-white/75 max-w-md">
              These are the outcomes clients hire Kostaja to produce. Read the six and notice which two matter most for your business right now. That is where we start.
            </p>
          </div>
          <div className="lg:col-span-7">
            <ul className="grid grid-cols-1 sm:grid-cols-2 gap-px bg-white/10 border border-white/15">
              {HOME_HELP_FIX.map((it, idx) => (
                <li
                  key={it.title}
                  data-testid={`home-help-fix-${idx}`}
                  className="bg-black p-6 lg:p-7 flex flex-col gap-2"
                >
                  <span className="text-[10px] uppercase tracking-[0.22em] font-bold k-yellow">0{idx + 1}</span>
                  <h3 className="text-lg lg:text-xl font-black tracking-tight leading-tight">{it.title}</h3>
                  <p className="text-sm text-white/75 leading-snug">{it.body}</p>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* WHERE TO START */}
      <section className="hidden" data-testid="home-how-to-start">
        <div className="max-w-[1440px] mx-auto px-5 lg:px-10 py-20 lg:py-28">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 mb-12">
            <div className="lg:col-span-6">
              <Overline className="mb-4">Where to start</Overline>
              <h2 className="text-4xl lg:text-6xl font-black tracking-tight leading-[0.95]">
                Six ways to begin.
              </h2>
            </div>
            <div className="lg:col-span-6 lg:pt-10">
              <p className="text-white/70 max-w-md">
                Pick the one that matches the loudest version of the problem. If two feel right, start a strategy call first so we name the sequence together.
              </p>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-white/10 border border-white/15">
            {HOME_HOW_TO_START.map((it, idx) => (
              <Link
                key={it.to}
                to={it.to}
                data-testid={`home-start-${idx}`}
                className="group bg-black p-6 lg:p-8 flex flex-col gap-3 hover:bg-[#FFC600] hover:text-black transition-colors"
              >
                <span className="text-[10px] uppercase tracking-[0.22em] font-bold opacity-70">0{idx + 1}.</span>
                <h3 className="text-xl lg:text-2xl font-black tracking-tight leading-tight">{it.title}</h3>
                <p className="text-sm opacity-80 leading-snug">{it.body}</p>
                <span className="mt-2 text-sm font-semibold inline-flex items-center gap-2">Open the path <span aria-hidden>→</span></span>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* FEATURED SERVICES */}
      <section className="hidden" data-testid="featured-services">
        <div className="max-w-[1440px] mx-auto px-5 lg:px-10 py-20 lg:py-28">
          <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6 mb-12">
            <div className="max-w-2xl">
              <Overline className="mb-4">Featured services</Overline>
              <h2 className="text-4xl lg:text-6xl font-black tracking-tighter leading-[0.95]">
                Outcomes you can actually buy.
              </h2>
              <p className="mt-4 text-white/70">
                Six places to start, each with a clear outcome and a clear next step.
              </p>
            </div>
            <div className="flex gap-3">
              <SecondaryButton to="/services" testId="featured-all">All services</SecondaryButton>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
            {featuredServices.map((s) => (
              <ServiceCard key={s.slug} service={s} />
            ))}
          </div>
        </div>
      </section>

      {/* CATEGORIES */}
      <section className="hidden" data-testid="categories">
        <div className="max-w-[1440px] mx-auto px-5 lg:px-10 py-20 lg:py-28">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 mb-10">
            <div className="lg:col-span-6">
              <Overline color="yellow" className="mb-4">Categories</Overline>
              <h2 className="text-4xl lg:text-6xl font-black tracking-tighter leading-[0.95]">
                Twelve categories. One disciplined studio.
              </h2>
            </div>
            <div className="lg:col-span-6 lg:pt-12">
              <p className="text-white/70 max-w-md">
                Browse the work by the category that matches the problem you are solving. Each one ladders into a clear scope and a clear price.
              </p>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-white/10 border border-white/10">
            {CATEGORIES.map((c, idx) => (
              <Link
                key={c.slug}
                to={categoryHref(c.slug)}
                data-testid={`category-${c.slug}`}
                className="group bg-black p-6 lg:p-8 flex items-start justify-between hover:bg-[#FFC600] hover:text-black transition-colors"
              >
                <div>
                  <div className="text-[10px] uppercase tracking-[0.22em] font-semibold opacity-70">
                    0{idx + 1}.
                  </div>
                  <div className="mt-2 text-xl lg:text-2xl font-black tracking-tighter leading-tight">
                    {c.name}
                  </div>
                </div>
                <span className="text-2xl">→</span>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* NEEDS + INDUSTRIES BROWSING */}
      <section className="hidden" data-testid="browse">
        <div className="max-w-[1440px] mx-auto px-5 lg:px-10 py-20 lg:py-28 grid grid-cols-1 lg:grid-cols-2 gap-10">
          <div>
            <Overline className="mb-4">Browse by need</Overline>
            <h3 className="text-3xl lg:text-5xl font-black tracking-tighter leading-[0.95]">
              Describe the problem. We name the next step.
            </h3>
            <ul className="mt-8 divide-y divide-white/10 border-y border-white/15">
              {NEEDS.map((n) => (
                <li key={n.slug}>
                  <Link
                    to={`/needs/${n.slug}`}
                    data-testid={`need-${n.slug}`}
                    className="flex items-center justify-between py-4 group"
                  >
                    <span className="text-base lg:text-lg font-semibold group-hover:text-[#FFC600] transition-colors">
                      {n.name}
                    </span>
                    <span className="text-white/40 group-hover:text-[#FFC600] transition-colors">→</span>
                  </Link>
                </li>
              ))}
            </ul>
          </div>
          <div>
            <Overline className="mb-4">Browse by industry</Overline>
            <h3 className="text-3xl lg:text-5xl font-black tracking-tighter leading-[0.95]">
              Pick the world you work in.
            </h3>

            {/* Real Estate spotlight tile */}
            {realEstate ? (
              <Link
                to={`/industries/${realEstate.slug}`}
                data-testid="home-industry-real-estate"
                className="mt-8 block bg-[#FFC600] text-black p-6 lg:p-7 border border-white/15 hover:k-hard-shadow"
              >
                <div className="flex items-center justify-between gap-4">
                  <div>
                    <div className="text-[10px] uppercase tracking-[0.22em] font-semibold opacity-70">Featured industry</div>
                    <div className="mt-2 text-3xl lg:text-4xl font-black tracking-tighter leading-tight">Real Estate</div>
                    <p className="mt-2 text-sm text-white/80 max-w-md">Personal brand, website, listing materials, and social presence for agents, teams, brokerages, mortgage professionals, and developers.</p>
                  </div>
                  <span aria-hidden className="text-3xl">→</span>
                </div>
              </Link>
            ) : null}

            <ul className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-px bg-white/10 border border-white/15">
              {otherIndustries.map((i) => (
                <li key={i.slug} className="bg-black">
                  <Link
                    to={`/industries/${i.slug}`}
                    data-testid={`industry-${i.slug}`}
                    className="block p-5 hover:bg-[#FFC600] transition-colors"
                  >
                    <div className="text-base lg:text-lg font-bold leading-tight">{i.name}</div>
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* PORTFOLIO TEASE */}
      <section className="bg-black border-b border-white/10" data-testid="home-portfolio">
        <div className="max-w-[1440px] mx-auto px-5 lg:px-10 py-20 lg:py-28">
          <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6 mb-12">
            <div className="max-w-2xl">
              <Overline color="yellow" className="mb-4">Selected work</Overline>
              <h2 className="text-4xl lg:text-6xl font-black tracking-[-0.02em] leading-[0.95]">
                Selected systems from the Kostaja world.
              </h2>
              <p className="mt-5 text-white/70 text-base lg:text-lg max-w-xl">
                Client work, owned brands, and internal concepts shaped through brand, website, content, search, proof, and buyer-path thinking.
              </p>
            </div>
            <SecondaryButton to="/portfolio" testId="home-portfolio-cta">View Selected Work</SecondaryButton>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 lg:gap-8">
            {PORTFOLIO.slice(0, 4).map((p) => (
              <Link
                key={p.slug}
                to={`/portfolio/${p.slug}`}
                data-testid={`home-portfolio-${p.slug}`}
                className="group block border border-white/15 overflow-hidden hover:k-hard-shadow"
              >
                <div className="aspect-[16/10] bg-black overflow-hidden">
                  <PortfolioCover item={p} accent="yellow" />
                </div>
                <div className="p-6 lg:p-8 bg-black flex items-start justify-between">
                  <div>
                    <div className="flex items-center gap-3 flex-wrap">
                      <span className="text-[10px] uppercase tracking-[0.22em] text-[#FFC600] font-bold">{p.type}</span>
                      <span className="text-white/30">·</span>
                      <span className="text-[10px] uppercase tracking-[0.22em] text-white/55 font-semibold">{p.category}</span>
                    </div>
                    <h3 className="text-2xl lg:text-3xl font-black mt-2 tracking-tighter leading-tight">
                      {p.title}
                    </h3>
                    <p className="text-sm text-white/70 mt-2">{p.summary}</p>
                  </div>
                  <span className="text-2xl group-hover:text-[#FFC600] transition-colors">→</span>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="bg-black border-b border-white/10" data-testid="home-faq">
        <div className="max-w-[1440px] mx-auto px-5 lg:px-10 py-20 lg:py-28 grid grid-cols-1 lg:grid-cols-12 gap-10">
          <div className="lg:col-span-5">
            <Overline className="mb-4">Frequently asked</Overline>
            <h2 className="text-4xl lg:text-6xl font-black tracking-tighter leading-[0.95]">
              Useful before the first call.
            </h2>
            <p className="mt-5 text-white/70">
              If something is missing here, send it on the contact page and we will answer in plain English.
            </p>
          </div>
          <div className="lg:col-span-7 divide-y divide-white/15 border-y border-white/10">
            {FAQS.map((f, idx) => (
              <details
                key={f.q}
                data-testid={`faq-${idx}`}
                className="group py-6"
              >
                <summary className="flex items-center justify-between cursor-pointer list-none">
                  <span className="text-lg lg:text-xl font-bold tracking-tight pr-6">{f.q}</span>
                  <span className="text-2xl text-white group-open:text-[#FFC600] transition-colors">＋</span>
                </summary>
                <p className="mt-3 text-white/70 leading-relaxed">{f.a}</p>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* LEARN MORE - COMING SOON */}
      <section className="hidden" data-testid="home-learn-more">
        <div className="max-w-[1440px] mx-auto px-5 lg:px-10 py-20 lg:py-28">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 mb-10">
            <div className="lg:col-span-6">
              <Overline className="mb-4">Learn more before you decide</Overline>
              <h2 className="text-4xl lg:text-6xl font-black tracking-tight leading-[0.95]">
                Plain English notes and Radio Kostaja.
              </h2>
            </div>
            <div className="lg:col-span-6 lg:pt-10">
              <p className="text-white/70 max-w-md">
                Short reads and episodes written to make the next decision easier. None of these are published yet. They are listed so you know what is coming.
              </p>
            </div>
          </div>
          <ul className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-white/10 border border-white/15">
            {LEARN_MORE.slice(0, 6).map((l) => (
              <LearnMoreCard key={l.slug} item={l} testId={`home-learn-${l.slug}`} />
            ))}
          </ul>
        </div>
      </section>

      {/* AUTHORITY HUB */}
      <section className="hidden" data-testid="home-authority-hub">
        <div className="max-w-[1440px] mx-auto px-5 lg:px-10 py-20 lg:py-28">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 mb-10">
            <div className="lg:col-span-6">
              <Overline className="mb-3">More on how Kostaja works</Overline>
              <h2 className="text-3xl lg:text-5xl font-black tracking-tight leading-[0.95]">
                Read further before you buy.
              </h2>
            </div>
            <div className="lg:col-span-6 lg:pt-10">
              <p className="text-white/70 max-w-md">
                Six long-form pages written to help you decide, whether you eventually hire Kostaja or not.
              </p>
            </div>
          </div>
          <ul className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-white/10 border border-white/15">
            {[
              { to: "/why-kostaja", eyebrow: "Why Kostaja", h: "Why service businesses choose Kostaja" },
              { to: "/best-brand-marketing-agency-canada", eyebrow: "Buyer guide", h: "How to choose a brand marketing agency in Canada" },
              { to: "/service-business-marketing-canada", eyebrow: "Category", h: "Marketing for Canadian service businesses" },
              { to: "/local-marketing-systems", eyebrow: "System build", h: "Local marketing systems for service businesses" },
              { to: "/ai-search-visibility-for-service-businesses", eyebrow: "AI answer engines", h: "AI search visibility for service businesses" },
              { to: "/website-branding-social-ai-for-service-businesses", eyebrow: "Multi-service", h: "Website, brand, social, and AI for service businesses" },
            ].map((it) => (
              <li key={it.to} className="bg-black">
                <Link
                  to={it.to}
                  data-testid={`home-authority-${it.to.slice(1)}`}
                  className="group block p-6 lg:p-7 hover:bg-[#FFC600] hover:text-black transition-colors h-full"
                >
                  <div className="text-[10px] uppercase tracking-[0.22em] font-bold k-yellow group-hover:text-black">{it.eyebrow}</div>
                  <div className="mt-3 text-lg lg:text-xl font-black tracking-tight leading-tight">{it.h}</div>
                  <div className="mt-4 text-[10px] uppercase tracking-[0.22em] font-bold text-white/55 group-hover:text-black/70">Read →</div>
                </Link>
              </li>
            ))}
          </ul>
        </div>
      </section>

      <GrowthLinks testId="home-growth-links" />

      <CTABlock variant="default" />
    </main>
  );
};

export default Home;
